<script setup lang="ts">
import { inject, watch } from "vue";
import { useGeneratedTable } from "@/composables/generated/useGeneratedTable";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useUtility } from "@/composables/useUtility";
import type { DirtyFlag } from "@/composables/useDirtyFlag";
import { useCardService } from "../../../cbi-generator/frontend/composables/useCardFrontendService";
import type { CardStaffSearchParams } from "../../../cbi-generator/generated/dto/CardStaffSearchParams";
import type { CardStaffSearchResult } from "../../../cbi-generator/generated/dto/CardStaffSearchResult";
import {
  CardStaffSearchParamsMeta,
  CardStaffSearchParamsMetaFields,
} from "../../../cbi-generator/generated/dto/CardStaffSearchParamsMeta";
import { CardStaffSearchResultMeta } from "../../../cbi-generator/generated/dto/CardStaffSearchResultMeta";
import type { CardMemberSearchParams } from "../../../cbi-generator/generated/dto/CardMemberSearchParams";
import type { CardMemberSearchResult } from "../../../cbi-generator/generated/dto/CardMemberSearchResult";
import { CardMemberSearchParamsMeta } from "../../../cbi-generator/generated/dto/CardMemberSearchParamsMeta";
import { CardMemberSearchResultMeta } from "../../../cbi-generator/generated/dto/CardMemberSearchResultMeta";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import ReportingTableServer from "@/components/shared/table/ReportingTableServer.vue";
import TableFilterControl from "@/components/shared/table/TableFilterControl.vue";
import GeneratedPersonCardDialog from "./GeneratedPersonCardDialog.vue";

const props = withDefaults(
  defineProps<{ personId: number; mode?: "staff" | "member" }>(),
  { mode: "staff" },
);
type Row = CardStaffSearchResult | CardMemberSearchResult;
const isMember = props.mode === "member";
const service = useCardService();
const { formatDateNY } = useUtility();
const staffConfig: GeneratedTableConfig<
  CardStaffSearchResult,
  CardStaffSearchParams
> = {
  id: `person-${props.personId}-cards`,
  rowKey: "assignId",
  resultMeta: CardStaffSearchResultMeta,
  searchMeta: CardStaffSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<CardStaffSearchParams>(CardStaffSearchParamsMeta),
    personIdExact: props.personId,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchCardStaff(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: ["cardNum", "assignId", "issueDate", "active", "actions"],
  defaultSort: [{ key: "issueDate", order: "desc" }],
};
// Member search is scoped to the caller's household server-side; card edits are staff-only.
const memberConfig: GeneratedTableConfig<
  CardMemberSearchResult,
  CardMemberSearchParams
> = {
  ...staffConfig,
  rowKey: "assignId",
  resultMeta: CardMemberSearchResultMeta,
  searchMeta: CardMemberSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<CardMemberSearchParams>(CardMemberSearchParamsMeta),
    personIdExact: props.personId,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchCardMember(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: ["cardNum", "assignId", "issueDate", "active"],
};
const { table, error } = useGeneratedTable<Row, CardStaffSearchParams>(
  (isMember ? memberConfig : staffConfig) as GeneratedTableConfig<
    Row,
    CardStaffSearchParams
  >,
);
table.extraHeaders.value = [
  { key: "cardNum", title: "Card Number" },
  { key: "assignId", title: "ID" },
  {
    key: "issueDate",
    title: "Date Issued",
    computeValue: (row) => formatDateNY(row.issueDate),
  },
  {
    key: "active",
    title: "Status",
    computeValue: (row) => (row.active ? "Active" : "Inactive"),
  },
  ...(isMember ? [] : [{ key: "actions", title: "Actions", sortable: false }]),
];
table.resetVisibleHeaders();
const filterMeta = {
  cardNumLike: CardStaffSearchParamsMetaFields.cardNumLike,
  activeExact: CardStaffSearchParamsMetaFields.activeExact,
};
for (const flag of [
  inject<DirtyFlag | undefined>("cardDataState", undefined),
  inject<DirtyFlag | undefined>("dirtyFlag", undefined),
]) {
  if (flag)
    watch(
      () => flag.isDirty,
      async (dirty) => {
        if (dirty) {
          flag.update(false);
          await table.reloadItems();
        }
      },
    );
}
const actionsSlot = "item.actions";
defineExpose({ table });
</script>

<template>
  <TableFilterControl
    :table="table"
    :filter-meta="filterMeta"
    :hide-export="isMember"
  />
  <v-alert v-if="error" type="error" role="alert" class="mb-4">
    {{ error }} Previous results may be shown.
  </v-alert>
  <ReportingTableServer :table="table">
    <template v-if="!isMember" v-slot:[actionsSlot]="{ item }">
      <GeneratedPersonCardDialog
        :person-id="personId"
        :card="item"
        @saved="table.reloadItems"
      />
    </template>
  </ReportingTableServer>
</template>
